import * as React from 'react';
import { matchPath, useNavigate } from 'react-router-dom';

import {
  getCurrentTopLevelSection,
  getFirstAvailableTopLevelSectionRoute,
} from '@/configs/organizationSectionsRouting';
import { ROUTES } from '@/configs/routes';
import type { TopLevelSectionSlug } from '@/entities/OrganizationSection';
import type { OrganizationSectionsStore } from '@/stores/local/shared/OrganizationSectionsStore';

type UseOrganizationSectionsAccessGuardParams = {
  id?: string;
  pathname: string;
  isPhone: boolean;
  normalizedOrganizationId: number;
  enabledTopLevelSections: TopLevelSectionSlug[];
  hasEnabledTopLevelSections: boolean;
  isSectionsConfigLoading: boolean;
  isSectionsConfigError: boolean;
  organizationSectionsStore: OrganizationSectionsStore;
};

export const useOrganizationSectionsAccessGuard = ({
  id,
  pathname,
  isPhone,
  normalizedOrganizationId,
  enabledTopLevelSections,
  hasEnabledTopLevelSections,
  isSectionsConfigLoading,
  isSectionsConfigError,
  organizationSectionsStore,
}: UseOrganizationSectionsAccessGuardParams): void => {
  const navigate = useNavigate();
  const currentTopLevelSection = React.useMemo(() => getCurrentTopLevelSection(pathname), [pathname]);

  React.useEffect(() => {
    if (isSectionsConfigLoading || isSectionsConfigError) {
      return;
    }

    if (!id || !currentTopLevelSection) {
      return;
    }

    if (organizationSectionsStore.isSectionEnabled(currentTopLevelSection, normalizedOrganizationId)) {
      return;
    }

    if (currentTopLevelSection === 'chat') {
      const chatMatch = matchPath(ROUTES.organization.documents.chat.path, pathname);
      const hasDocumentContext = Boolean(chatMatch?.params?.documentId);

      if (hasDocumentContext && organizationSectionsStore.isSectionEnabled('documents', normalizedOrganizationId)) {
        return;
      }
    }

    const fallbackRoute = getFirstAvailableTopLevelSectionRoute({
      enabledSections: enabledTopLevelSections,
      organizationId: id,
      isPhone,
    });

    if (fallbackRoute) {
      navigate(fallbackRoute, { replace: true });

      return;
    }

    if (!hasEnabledTopLevelSections) {
      return;
    }

    navigate(ROUTES.root.path, { replace: true });
  }, [
    currentTopLevelSection,
    enabledTopLevelSections,
    hasEnabledTopLevelSections,
    id,
    isSectionsConfigError,
    isSectionsConfigLoading,
    isPhone,
    navigate,
    pathname,
    normalizedOrganizationId,
    organizationSectionsStore,
  ]);
};
